import { useState } from 'react';
import { Button, Input } from '@adop/ui';
import { getSupabase } from '../lib/supabase';
import { devAuthEnabledFromImportMeta } from '../lib/env';
import { errorMessage } from '../lib/api';
import { Alert } from './Alert';

/**
 * LOCAL-ONLY email/password sign-in. This component is never rendered in a
 * production build: `devAuthEnabledFromImportMeta` returns false whenever Vite
 * sets `import.meta.env.PROD`.
 */
export function DevAuthForm({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!devAuthEnabledFromImportMeta()) return null;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result =
        mode === 'signin'
          ? await getSupabase().auth.signInWithPassword({ email, password })
          : await getSupabase().auth.signUp({ email, password });
      if (result.error) throw result.error;
      if (mode === 'signup' && !result.data.session) {
        setError('Account created. Check your email to confirm, then sign in.');
        return;
      }
      onSignedIn();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 rounded-lg border border-dashed border-amber-300 bg-amber-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Dev sign-in</p>
      <Input
        label="Email"
        type="email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        autoComplete="email"
      />
      <Input
        label="Password"
        type="password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
      />
      {error ? <Alert>{error}</Alert> : null}
      <div className="flex items-center gap-2">
        <Button loading={busy} onClick={submit} disabled={!email || !password}>
          {mode === 'signin' ? 'Sign in' : 'Sign up'}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')}>
          {mode === 'signin' ? 'Create account' : 'Have an account?'}
        </Button>
      </div>
    </div>
  );
}
